import { IconButton, Tooltip } from '@material-ui/core';
import Menu from '@material-ui/core/Menu';
import MenuItem from '@material-ui/core/MenuItem';
import Typography from '@material-ui/core/Typography';
import {
  Block as BlockIcon,
  MoreHoriz as MoreHorizIcon,
  Search as PageviewIcon,
  Receipt as ReceiptIcon,
} from '@material-ui/icons';
import { isEmpty, isFunction } from 'lodash';
import { bindMenu, bindTrigger } from 'material-ui-popup-state';
import { useCallback, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory } from 'react-router';
import { userHasDeleteRole } from '../../../../../components/perfil/perfil-utils';
import QCXPopupState from '../../../../../components/popup-state/QCXPopupState';
import { setQuery } from '../../../../../features/controller/controllerSlice';
import QCXConfirmDialog from '../../../../../shared-components/dialog/QCXConfirmDialog';
import QCXListItemIcon from '../../../../../shared-components/list-item-icon/QCXListItemIcon';
import SimpleConsultPageTemplate from '../../../../../templates/simple-consult-page/SimpleConsultPageTemplate/SimpleConsultPageTemplate';
import { formatDate } from '../../../../../utils/general/general-utils';
import { formatCnpj } from '../../../../../utils/hooks/form/field/formatters';
import useOperationMiddleware from '../../../../../utils/hooks/operation/middleware/useOperationMiddleware';
import { isLoadingStatus, isRefresh } from '../../../../../utils/store/store-utils';
import {
  selectDadosEmbarqueExpCount,
  selectDadosEmbarques,
  selectHistoryModel,
  selectRefresh,
  selectStatus,
} from '../api/dadosEmbarqueSelectors';
import {
  changeToConsultMode,
  resetBackgroundMode,
  resetMode,
  resetModel,
  resetRefresh,
  resetSubMode,
} from '../api/dadosEmbarqueSlice';
import {
  activateByIdAsync,
  fetchAllAsync,
  fetchAllDadosEmbarqueExportacaoByFilterAsync,
  inactivateByIdAsync,
} from '../api/dadosEmbarqueThunks';
import { convertUTCStringToLocalDateTime } from '../../../../../utils/general/dateTime-utils';

export default function ExportacaoDadosEmbarqueConsultPage({ authInfo = {} }) {
  const { t } = useTranslation();
  const history = useHistory();

  const dispatch = useDispatch();

  const refresh = useSelector(selectRefresh);
  const dadosEmbarques = useSelector(selectDadosEmbarques);
  const historyModel = useSelector(selectHistoryModel);
  const status = useSelector(selectStatus);

  const isLoading = useMemo(() => isLoadingStatus(status), [status]);

  const resetModes = useCallback(() => {
    dispatch(resetMode());
    dispatch(resetBackgroundMode());
    dispatch(resetSubMode());
  }, []);

  const resetModels = useCallback(() => {
    dispatch(resetModel());
  }, []);

  useEffect(() => {
    resetModes();
    resetModels();
  }, []);

  useEffect(() => {
    const checkIfIsRefresh = () => {
      if (isRefresh(refresh)) {
        dispatch(fetchAllAsync());
        dispatch(resetRefresh());
      }
    };

    checkIfIsRefresh();
  }, [refresh]);

  useEffect(() => {
    const checkIfExistsQuery = () => {
      if (!isEmpty(historyModel) && historyModel?.last?.numero) {
        dispatch(setQuery(historyModel?.last?.numero));
      }
    };

    checkIfExistsQuery();
  }, [historyModel]);

  const handleConsultarClick = useCallback(
    (event, data) => {
      if (isFunction(event?.stopPropagation)) {
        event.stopPropagation();

        dispatch(changeToConsultMode());

        history.push(t('com.muralis.qcx.url.exportacaoDadosEmbarqueDetalhes', { id: data?.id }));
      }
    },
    [history]
  );

  const [handleActivateOrInactivateByIdClickMiddleware, operationOfActivateOrInactivateById] = useOperationMiddleware(
    async (data) => {
      const isInactivation = data?.active;

      if (isInactivation) {
        dispatch(inactivateByIdAsync(data?.id));
        return;
      }

      dispatch(activateByIdAsync(data?.id));
    },
    [dadosEmbarques]
  );

  const handleConfigureActivateOrInactivateByIdClick = useCallback(
    (row, popupState) => {
      const { id, followUpNumero, active } = row;

      const handleActivateOrInactivateByIdClick = (event) => {
        if (isFunction(event?.stopPropagation)) {
          popupState.close(event);
          event.stopPropagation();

          const configureOperation = () => ({
            options: {
              title: t('com.muralis.qcx.acoes.confirmarOperacaoExclusao').toUpperCase(),
              message: t('com.muralis.qcx.acoes.confirmacaoAcaoDI', {
                acao: active ? t('com.muralis.qcx.acoes.excluir') : t('com.muralis.qcx.acoes.recuperar'),
                numero: followUpNumero,
              }),
            },
          });

          const dataOfOperationActivateOrInactivate = {
            id,
            active,
          };

          handleActivateOrInactivateByIdClickMiddleware(
            configureOperation,
            dataOfOperationActivateOrInactivate
          );
        }
      };

      return handleActivateOrInactivateByIdClick;
    },
    [handleActivateOrInactivateByIdClickMiddleware, t]
  );

  const columns = useMemo(
    () => [
      {
        field: 'followUpNumero',
        headerName: t('com.muralis.qcx.processo'),
        headerAlign: 'center',
        align: 'center',
        flex: 140,
        valueGetter: ({ row }) => row?.followUpNumero || '-',
      },
      {
        field: 'importadorNome',
        headerName: t('com.muralis.qcx.cliente.label'),
        flex: 180,
        valueGetter: ({ row }) => row?.importadorNome || '-',
      },
      {
        field: 'importadorCnpj',
        headerName: t('com.muralis.qcx.empresa.CNPJ'),
        flex: 140,
        valueGetter: ({ row }) => formatCnpj(row?.importadorCnpj) || '-',
      },
      {
        field: 'insertionDate',
        headerName: t('com.muralis.qcx.abertura'),
        headerAlign: 'center',
        align: 'center',
        type: 'date',
        flex: 100,
        valueGetter: ({ row }) =>
          formatDate(
            convertUTCStringToLocalDateTime(row?.insertionDate),
            'DD/MM/YYYY HH:mm:ss',
            'DD/MM/YYYY'
          ) || '-',
      },
      {
        field: 'actions',
        headerName: t('com.muralis.qcx.acoes.label'),
        headerAlign: 'center',
        align: 'center',
        flex: 120,
        renderCell: ({ row: { id, followUpNumero, active } }) => (
          <>
            <QCXPopupState popupId="popup-menu-follow-up">
              {(popupState) => (
                <>
                  <Tooltip title={t('com.muralis.qcx.maisOpcoes')} arrow>
                    <IconButton
                      key={`btn-more-options-${id}`}
                      name={`btn-more-options-${id}`}
                      {...bindTrigger(popupState)}
                    >
                      <MoreHorizIcon color="secondary" size={20} />
                    </IconButton>
                  </Tooltip>
                  <Menu {...bindMenu(popupState)}>
                    <MenuItem
                      onClick={(event) => {
                        popupState.close(event);

                        handleConsultarClick(event, {
                          id,
                        });
                      }}
                    >
                      <QCXListItemIcon>
                        <PageviewIcon fontSize="small" />
                      </QCXListItemIcon>
                      <Typography
                        variant="inherit"
                        style={{
                          fontSize: 12,
                        }}
                      >
                        {t('com.muralis.qcx.acoes.consultar').toUpperCase()}
                      </Typography>
                    </MenuItem>

                    {userHasDeleteRole(authInfo, 'Exportacao-follow-up') && (
                      <MenuItem
                        onClick={handleConfigureActivateOrInactivateByIdClick(
                          {
                            id,
                            followUpNumero,
                            active,
                          },
                          popupState
                        )}
                        disabled
                      >
                        <QCXListItemIcon>
                          <BlockIcon fontSize="small" color="disabled" />
                        </QCXListItemIcon>
                        <Typography
                          variant="inherit"
                          style={{
                            fontSize: 12,
                          }}
                        >
                          {t('com.muralis.qcx.acoes.cancelar').toUpperCase()}
                        </Typography>
                      </MenuItem>
                    )}
                  </Menu>
                </>
              )}
            </QCXPopupState>
          </>
        ),
      },
    ],
    [handleConsultarClick, handleConfigureActivateOrInactivateByIdClick, t, authInfo]
  );

  const breadcrumbs = [
    {
      link: {
        to: '/',
        name: t('com.muralis.qcx.inicio'),
      },
    },
    {
      link: {
        to: t('com.muralis.qcx.url.moduloExportacao'),
        name: t('com.muralis.qcx.exportacao.label'),
      },
    },
    {
      text: {
        name: t('com.muralis.qcx.dadosEmbarque.label'),
      },
      default: true,
    },
  ];

  const handleConfigChange = useCallback(({ currentPage, pageSize, filterInputValue }) => {
    dispatch(
      fetchAllDadosEmbarqueExportacaoByFilterAsync([
        { name: 'page', value: currentPage },
        { name: 'size', value: pageSize },
        { name: 'numero', value: filterInputValue },
      ])
    );
  }, []);

  const rowsCount = useSelector(selectDadosEmbarqueExpCount);

  const templateProps = useMemo(() => ({
    page: {
      title: t('com.muralis.qcx.dadosEmbarque.label'),
      icon: <ReceiptIcon />,
      breadcrumbs,
    },
    table: {
      columns,
      rowsCount,
      onConfigChangeCallback: handleConfigChange,
    },
  }));

  return (
    <SimpleConsultPageTemplate
      pageProps={templateProps.page}
      controlProps={templateProps.control}
      tableProps={templateProps.table}
      tableData={dadosEmbarques}
      isLoading={isLoading}
      authInfo={authInfo}
      requiredRoles={['exportacao-follow-up']}
    >
      <QCXConfirmDialog
        key="confirm-dialog-activate-inactivate-follow-up"
        id="confirm-dialog-activate-inactivate-follow-up"
        open={operationOfActivateOrInactivateById?.active}
        title={operationOfActivateOrInactivateById?.title}
        content={operationOfActivateOrInactivateById?.message}
        endContent={operationOfActivateOrInactivateById?.endMessage}
        onConfirm={operationOfActivateOrInactivateById.confirm}
        onClose={operationOfActivateOrInactivateById?.reset}
      />
    </SimpleConsultPageTemplate>
  );
}